import { motion, useReducedMotion } from "framer-motion";
import type { ComponentProps, ReactNode } from "react";

type Props = {
  children: ReactNode;
  delay?: number;
  /** Pixels of lift the element arrives from. */
  y?: number;
  once?: boolean;
  className?: string;
} & Omit<ComponentProps<typeof motion.div>, "children">;

/**
 * Scroll-linked entrance. Elements arrive from a little below with a soft
 * fade, once, when a third of them is in view. Respects reduced motion.
 */
export default function Reveal({ children, delay = 0, y = 28, once = true, className, ...rest }: Props) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.3, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Splits a headline into words that rise one after the other. */
export function RevealWords({ text, className, delay = 0 }: { text: string; className?: string; delay?: number }) {
  const reduce = useReducedMotion();
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((word, index) => (
        <span key={`${word}-${index}`} style={{ display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}>
          <motion.span
            style={{ display: "inline-block" }}
            initial={reduce ? false : { y: "110%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: delay + index * 0.06 }}
            aria-hidden="true"
          >
            {word}
          </motion.span>
          {index < words.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}
